import { chunk, debounce } from 'lodash';

import {
  CustomEventNames,
  GoogleAnalytics4EventNames,
  KCOEventNames,
} from '../AnalyticsEventNames';
import PaymentInfo from './Events/PaymentInfo';
import Promotion from './Events/Promotion';
import Purchase from './Events/Purchase';
import ShippingInfo from './Events/ShippingInfo';
import DataLayer from './Models/DataLayer';
import Item from './Models/Item';

const IMPRESSION_MAX_BATCH_SIZE = 35;

const WEBSTORE_DIMENSION = {
  dimensionKey: 'siteType',
  dimensionValue: 'webStore',
};

const PROPOSAL_DIMENSION = {
  dimensionKey: 'siteType',
  dimensionValue: 'proposal',
};

export default class GoogleAnalytics4EventHandler {
  /**
   * @private
   */
  #configStore;
  /**
   * @private
   */
  #currency;
  /**
   * @private
   */
  #precision;
  /**
   * @private
   */
  #activeCountry;
  /**
   * @private
   */
  #viewItemListDataQueue;
  /**
   * @private
   */
  #requestViewItemListPushToDataLayer;
  /**
   * @private
   */
  #item;
  /**
   * @private
   */
  #dataLayer;
  /**
   * @private
   * @type {Promotion}
   */
  #promotion;
  /**
   * @private
   * @type {ShippingInfo}
   */
  #shippingInfo;
  /**
   * @private
   * @type {PaymentInfo}
   */
  #paymentInfo;
  /**
   * @private
   * @type {Purchase}
   */
  #purchase;

  constructor(rootStore) {
    const { configStore, currencyStore, countryStore } = rootStore;

    this.#configStore = configStore;
    this.#activeCountry = countryStore.activeCountry;
    this.#precision = 4;
    this.#currency = currencyStore.currencyCode;

    this.#item = new Item(rootStore);

    this.#dataLayer = new DataLayer();

    this.#purchase = new Purchase(this.#item, this.#dataLayer, rootStore);
    this.#promotion = new Promotion(this.#item, this.#dataLayer, rootStore);
    this.#shippingInfo = new ShippingInfo(
      this.#item,
      this.#dataLayer,
      rootStore
    );
    this.#paymentInfo = new PaymentInfo(this.#item, this.#dataLayer, rootStore);

    // We use our own impressionDataQueue & promoViewDataQueue to hold the data in order to send it in batches
    this.#viewItemListDataQueue = [];

    // We don't want to spam Google Analytics, so we will always wait for 100ms for new data before sending impressions
    // and promoViews data out. If we have a constant flow of data, we will send data every 500 ms (maxWait).
    const wait = 100; // ms
    const maxWait = 500; // ms
    this.#requestViewItemListPushToDataLayer = debounce(
      this.#pushViewItemListToDataLayer,
      wait,
      {
        maxWait,
      }
    );
  }

  handleAnalyticsEvents = (event) => {
    /**
     * Official Google events should be at the top
     * Custom events are located after viewPromotion
     */
    switch (event.detail.name) {
      case GoogleAnalytics4EventNames.addPaymentInfo:
        this.#addPaymentInfo(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.addShippingInfo:
        this.#addShippingInfo(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.addToCart:
        this.#addToCart(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.addToProposal:
        this.#addToProposal(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.addToWishlist:
        this.#addToWishlist(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.beginCheckout:
        this.#beginCheckout(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.beginProposal:
        this.#beginProposal(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.generateLead:
        this.#generateLead(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.joinGroup:
        this.#joinGroup(event.detail.payload);
        break;
      // KCO (Kustom Checkout) events - based on official KCO documentation
      case KCOEventNames.load:
        this.#kcoLoad(event.detail.payload);
        break;
      case KCOEventNames.customer:
        this.#kcoCustomer(event.detail.payload);
        break;
      case KCOEventNames.change:
        this.#kcoChange(event.detail.payload);
        break;
      case KCOEventNames.billing_address_change:
        this.#kcoBillingAddressChange(event.detail.payload);
        break;
      case KCOEventNames.shipping_address_change:
        this.#kcoShippingAddressChange(event.detail.payload);
        break;
      case KCOEventNames.shipping_option_change:
        this.#kcoShippingOptionChange(event.detail.payload);
        break;
      case KCOEventNames.order_total_change:
        this.#kcoOrderTotalChange(event.detail.payload);
        break;
      case KCOEventNames.checkbox_change:
        this.#kcoCheckboxChange(event.detail.payload);
        break;
      case KCOEventNames.network_error:
        this.#kcoNetworkError(event.detail.payload);
        break;
      case KCOEventNames.redirect_initiated:
        this.#kcoRedirectInitiated(event.detail.payload);
        break;
      case KCOEventNames.load_confirmation:
        this.#kcoLoadConfirmation(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.login:
        this.#login(event.detail.payload);
        break;
      case CustomEventNames.payment_start:
        this.#paymentStart(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.proposalSent:
        this.#proposalSent(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.purchase:
        this.#sendPurchase(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.removeFromCart:
        this.#removeFromCart(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.removeFromProposal:
        this.#removeFromProposal(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.search:
        this.#search(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.selectItem:
        this.#selectItem(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.selectPromotion:
        this.#selectPromotion(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.share:
        this.#share(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.signup:
        this.#signup(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.viewCart:
        this.#viewCart(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.viewProposal:
        this.#viewProposal(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.viewItem:
        this.#viewItem(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.viewItemList:
        this.#viewItemList(event.detail.payload);
        break;
      case GoogleAnalytics4EventNames.viewPromotion:
        this.#viewPromotion(event.detail.payload);
        break;
      case CustomEventNames.sendGdprData:
        this.#sendGdprData(event.detail.payload);
        break;
      case CustomEventNames.suggestionSelection:
        this.#suggestionSelection(event.detail.payload);
        break;
      case CustomEventNames.pageNotFound:
        this.#sendPageNotFoundError(event.detail.payload);
        break;
      default:
    }
  };

  /**
   * @param {Object} paymentInfo
   * @param {string} paymentInfo.currencyCode
   * @param {number} paymentInfo.value
   * @param {Array.<AnalyticsItem>} paymentInfo.productList
   */
  #addPaymentInfo = ({ currencyCode, value, productList }) => {
    this.#paymentInfo.sendPaymentInfoEvent({
      productList,
      currencyCode,
      value,
    });
  };

  /**
   * @param {Object} shippingInfo
   * @param {string} shippingInfo.currencyCode
   * @param {number} shippingInfo.value
   * @param {Array.<AnalyticsItem>} shippingInfo.productList
   */
  #addShippingInfo = ({ currencyCode, value, productList }) => {
    this.#shippingInfo.sendShippingInfoEvent({
      productList,
      currencyCode,
      value,
    });
  };

  /**
   * @param {Object} cart
   * @param {string} cart.currencyCode
   * @param {number} cart.value
   * @param {Array.<AnalyticsItem>} cart.productList
   */
  #addToCart = ({ currencyCode, value, productList }) => {
    const data = {
      ecommerce: {
        currency: currencyCode,
        value,
        items: productList.map((product) =>
          this.#item.analyticsItemToItem(product)
        ),
      },
    };

    this.#pushEventToDataLayer(
      data,
      GoogleAnalytics4EventNames.addToCart,
      this.#updateSiteTypeDimension(WEBSTORE_DIMENSION)
    );
  };

  /**
   * @param {Object} cart
   * @param {string} cart.currencyCode
   * @param {number} cart.value
   * @param {Array.<AnalyticsItem>} cart.productList
   */
  #addToProposal = ({ currencyCode, value, productList }) => {
    const data = {
      ecommerce: {
        currency: currencyCode,
        value,
        items: productList.map((product) =>
          this.#item.analyticsItemToItem(product)
        ),
      },
    };

    this.#pushEventToDataLayer(
      data,
      GoogleAnalytics4EventNames.addToCart,
      this.#updateSiteTypeDimension(PROPOSAL_DIMENSION)
    );
  };

  /**
   * @param {Object} cart
   * @param {string} cart.currencyCode
   * @param {number} cart.value
   * @param {Array.<AnalyticsItem>} cart.productList
   */
  #addToWishlist = ({ currencyCode, value, productList }) => {
    const data = {
      ecommerce: {
        currency: currencyCode,
        value,
        items: productList.map((product) =>
          this.#item.analyticsItemToItem(product)
        ),
      },
    };
    this.#pushEventToDataLayer(data, GoogleAnalytics4EventNames.addToWishlist);
  };

  /**
   * @param {Object} checkout
   * @param {string} checkout.currencyCode
   * @param {Array.<AnalyticsItem>} checkout.productList
   * @param {number} checkout.value
   * @param {string} checkout.coupon
   */
  #beginCheckout = ({ currencyCode, productList, value, coupon }) => {
    const data = {
      ecommerce: {
        currency: currencyCode,
        value,
        items: productList.map((product) =>
          this.#item.analyticsItemToItem(
            this.createCartItemObject({ product }),
            true
          )
        ),
      },
    };

    if (coupon) {
      data.ecommerce.coupon = coupon;
    }

    this.#pushEventToDataLayer(
      data,
      GoogleAnalytics4EventNames.beginCheckout,
      this.#updateSiteTypeDimension(WEBSTORE_DIMENSION)
    );
  };

  /**
   * @param {Object} checkout
   * @param {string} checkout.currencyCode
   * @param {Array.<AnalyticsItem>} checkout.productList
   * @param {number} checkout.value
   */
  #beginProposal = ({ currencyCode, productList, value }) => {
    const data = {
      ecommerce: {
        currency: currencyCode,
        value,
        items: productList.map((product) =>
          this.#item.analyticsItemToItem(
            this.createCartItemObject({ product }),
            true
          )
        ),
      },
    };

    this.#pushEventToDataLayer(
      data,
      GoogleAnalytics4EventNames.beginCheckout,
      this.#updateSiteTypeDimension(PROPOSAL_DIMENSION)
    );
  };

  /**
   * @param {Object} generateLead
   * @param {string} generateLead.currencyCode
   * @param {number} generateLead.value
   * @param {string} generateLead.lead_source
   */
  #generateLead = ({ currencyCode, value, lead_source }) => {
    const data = {
      ecommerce: {
        currency: currencyCode,
        value: Number(value.toFixed(this.#precision)),
        lead_source,
      },
    };

    this.#pushEventToDataLayer(data, GoogleAnalytics4EventNames.generateLead);
  };

  /**
   * @param {Object} joinGroup
   * @param {string} joinGroup.groupId The ID of the group
   */
  #joinGroup = ({ groupId }) => {
    const data = {
      group_id: groupId,
    };

    this.#pushEventToDataLayer(data, GoogleAnalytics4EventNames.joinGroup);
  };

  // KCO (Kustom Checkout) event handlers
  #kcoLoad = (eventData) => {
    // eventData: { customer: { type: "person|organization" }, shipping_address: { country: "swe", postal_code: "16972" } }
    const data = {
      kco_event_type: 'load',
      kco_customer_type: eventData.customer?.type,
      kco_shipping_country: eventData.shipping_address?.country,
      kco_shipping_postal_code: eventData.shipping_address?.postal_code,
    };
    this.#pushEventToDataLayer(data, KCOEventNames.load);
  };

  #kcoCustomer = (eventData) => {
    // eventData: { type: "person|organization" }
    const data = {
      kco_event_type: 'customer',
      kco_customer_type: eventData.type,
    };
    this.#pushEventToDataLayer(data, KCOEventNames.customer);
  };

  #kcoChange = (eventData) => {
    // eventData: { email: "...", postal_code: "...", country: "..." }
    const data = {
      kco_event_type: 'change',
      kco_email: eventData.email,
      kco_postal_code: eventData.postal_code,
      kco_country: eventData.country,
    };
    this.#pushEventToDataLayer(data, KCOEventNames.change);
  };

  #kcoBillingAddressChange = (eventData) => {
    // eventData: { postal_code: "75511", country: "aut" }
    const data = {
      kco_event_type: 'billing_address_change',
      kco_billing_postal_code: eventData.postal_code,
      kco_billing_country: eventData.country,
    };
    this.#pushEventToDataLayer(data, KCOEventNames.billing_address_change);
  };

  #kcoShippingAddressChange = (eventData) => {
    // eventData: { postal_code: "75511", country: "aut" }
    const data = {
      kco_event_type: 'shipping_address_change',
      kco_shipping_postal_code: eventData.postal_code,
      kco_shipping_country: eventData.country,
    };
    this.#pushEventToDataLayer(data, KCOEventNames.shipping_address_change);
  };

  #kcoShippingOptionChange = (eventData) => {
    // eventData: { description: "...", id: "...", name: "...", price: 99999, promo: "...", tax_amount: 7621, tax_rate: 825 }
    const data = {
      kco_event_type: 'shipping_option_change',
      kco_shipping_id: eventData.id,
      kco_shipping_name: eventData.name,
      kco_shipping_description: eventData.description,
      kco_shipping_price:
        eventData.price != null ? Number(eventData.price) / 100 : null, // convert from cents to currency units
      currency: this.#currency,
      kco_shipping_promo: eventData.promo,
      kco_shipping_tax_amount:
        eventData.tax_amount != null
          ? Number(eventData.tax_amount) / 100
          : null, // convert from cents to currency units
      kco_shipping_tax_rate:
        eventData.tax_rate != null ? Number(eventData.tax_rate) / 100 : null, // convert from cents to currency units
    };
    this.#pushEventToDataLayer(data, KCOEventNames.shipping_option_change);
  };

  #kcoOrderTotalChange = (eventData) => {
    // eventData: { order_total: 6000 }
    const data = {
      kco_event_type: 'order_total_change',
      kco_order_total:
        eventData.order_total != null
          ? Number(eventData.order_total) / 100
          : null, // convert from cents to currency units
      currency: this.#currency,
    };
    this.#pushEventToDataLayer(data, KCOEventNames.order_total_change);
  };

  #kcoCheckboxChange = (eventData) => {
    // eventData: { key: "additional_merchant_terms_checkbox", checked: true }
    const data = {
      kco_event_type: 'checkbox_change',
      kco_checkbox_key: eventData.key,
      kco_checkbox_checked: eventData.checked,
    };
    this.#pushEventToDataLayer(data, KCOEventNames.checkbox_change);
  };

  #kcoNetworkError = (eventData) => {
    // eventData: {} (empty object)
    const data = {
      kco_event_type: 'network_error',
    };
    this.#pushEventToDataLayer(data, KCOEventNames.network_error);
  };

  #kcoRedirectInitiated = (eventData) => {
    // eventData: true
    const data = {
      kco_event_type: 'redirect_initiated',
      kco_redirect_status: eventData,
    };
    this.#pushEventToDataLayer(data, KCOEventNames.redirect_initiated);
  };

  #kcoLoadConfirmation = (eventData) => {
    // eventData: {} (empty object)
    const data = {
      kco_event_type: 'load_confirmation',
    };
    this.#pushEventToDataLayer(data, KCOEventNames.load_confirmation);
  };

  /**
   * @param {Object} login
   * @param {string} login.method The method used to login.
   */
  #login = ({ method }) => {
    const data = {
      method,
    };

    this.#pushEventToDataLayer(data, GoogleAnalytics4EventNames.login);
  };

  #paymentStart = () => {
    this.#purchase.sendPaymentStartEvent(this.#currency);
  };

  #sendPurchase = () => {
    this.#purchase.sendPurchaseEvent(
      this.#updateSiteTypeDimension(WEBSTORE_DIMENSION)
    );
  };

  /**
   * @param {Object} proposalSent
   * @param {Order} proposalSent.currentOrder Current order model
   */
  #proposalSent = ({ currentOrder }) => {
    this.#pushEventToDataLayer(
      this.getSuccessData(currentOrder),
      GoogleAnalytics4EventNames.purchase,
      this.#updateSiteTypeDimension(PROPOSAL_DIMENSION)
    );
  };

  /**
   * @param {Object} removeFromCart
   * @param {string} removeFromCart.currencyCode
   * @param {Array.<AnalyticsItem>} removeFromCart.productList
   * @param {number} value a Monetary value of the event
   */
  #removeFromCart = ({ currencyCode, productList, value }) => {
    const data = {
      ecommerce: {
        currency: currencyCode,
        value,
        items: productList.map((product) =>
          this.#item.analyticsItemToItem(
            this.createCartItemObject({ product }),
            true
          )
        ),
      },
    };
    this.#pushEventToDataLayer(
      data,
      GoogleAnalytics4EventNames.removeFromCart,
      this.#updateSiteTypeDimension(WEBSTORE_DIMENSION)
    );
  };

  /**
   * @param {Object} removeFromCart
   * @param {string} removeFromCart.currencyCode
   * @param {Array.<CartProduct>} removeFromCart.productList
   */
  #removeFromProposal = ({ currencyCode, value, productList }) => {
    const data = {
      ecommerce: {
        currency: currencyCode,
        value,
        items: productList.map((product) =>
          this.#item.analyticsItemToItem(
            this.createCartItemObject({ product }),
            true
          )
        ),
      },
    };
    this.#pushEventToDataLayer(
      data,
      GoogleAnalytics4EventNames.removeFromCart,
      this.#updateSiteTypeDimension(PROPOSAL_DIMENSION)
    );
  };

  /**
   * @param {Object} searchTerm
   * @param {string} searchTerm.searchTerm
   */
  #search = ({ searchTerm }) => {
    const data = {
      search_term: searchTerm,
    };

    this.#pushEventToDataLayer(data, GoogleAnalytics4EventNames.search);
  };

  /**
   * @param {Object} suggestionSelection
   * @param {string} suggestionSelection.searchText
   * @param {string} suggestionSelection.suggestionType
   * @param {string} suggestionSelection.suggestionValue
   */
  #suggestionSelection = ({ searchText, suggestionType, suggestionValue }) => {
    const data = {
      search_term: searchText,
      suggestion_type: suggestionType,
      suggestion_value: suggestionValue,
    };

    this.#pushEventToDataLayer(data, CustomEventNames.suggestionSelection);
  };

  /**
   * @param {Object} gdprInfo
   * @param {string} gdprInfo.first_name
   * @param {string} gdprInfo.last_name
   * @param {string} gdprInfo.email
   * @param {string} gdprInfo.street_address
   * @param {string} gdprInfo.international_number
   * @param {boolean} gdprInfo.marketing_ban
   */
  #sendGdprData = ({
    first_name,
    last_name,
    email,
    international_number,
    street_address,
    marketing_ban,
  }) => {
    const data = {
      gdpr_data: {
        first_name,
        last_name,
        email,
        international_number,
        street_address,
        marketing_ban,
      },
    };

    this.#pushEventToDataLayer(data, CustomEventNames.sendGdprData);
  };

  /**
   * @param {Object} selectItem
   * @param {string} selectItem.listName
   * @param {Array.<AnalyticsItem>} selectItem.productList
   * @param {string} selectItem.listId
   */
  #selectItem = ({ listName, productList, listId }) => {
    const data = {
      ecommerce: {
        item_list_id: listId,
        item_list_name: listName,
        items: productList.map((product) =>
          this.#item.analyticsItemToItem(product)
        ),
      },
    };

    this.#pushEventToDataLayer(data, GoogleAnalytics4EventNames.selectItem);
  };

  /**
   * @param {Object} promotion
   * @param {Array.<AnalyticsItem>} promotion.bannerList An array with objects in the format { banner, bannerZone }.
   */
  #selectPromotion = ({ bannerList }) => {
    this.#promotion.sendSelectPromotionEvent(bannerList);
  };

  /**
   * @param {Object} content
   * @param {string} content.method
   * @param {string} content.contentType
   * @param {string} content.productId
   */
  #share = ({ content }) => {
    const data = {
      ...content,
    };

    this.#pushEventToDataLayer(data, GoogleAnalytics4EventNames.share);
  };

  /**
   * @param {Object} signup
   * @param {string} signup.method Method use to signup
   */
  #signup = ({ method }) => {
    const data = {
      method,
    };

    this.#pushEventToDataLayer(data, GoogleAnalytics4EventNames.signup);
  };

  /**
   * @param {Object} data
   * @param {Array.<AnalyticsItem>} data.productList
   * @param {number} data.value
   * @param {number} data.eventData
   */
  #viewCart = ({ productList, value, eventData }) => {
    const data = {
      ecommerce: {
        currency: this.#currency,
        value,
        items: productList.map((product) =>
          this.#item.analyticsItemToItem(
            this.createCartItemObject({ product, eventData }),
            true
          )
        ),
      },
    };
    this.#pushEventToDataLayer(
      data,
      GoogleAnalytics4EventNames.viewCart,
      this.#updateSiteTypeDimension(WEBSTORE_DIMENSION)
    );
  };

  /**
   * @param {Object} viewItem
   * @param {string} viewItem.currencyCode
   * @param {number} viewItem.value
   * @param {Array.<AnalyticsItem>} viewItem.productList
   */
  #viewItem = ({ currencyCode, value, productList }) => {
    const data = {
      ecommerce: {
        currency: currencyCode,
        value,
        items: productList.map((product) =>
          this.#item.analyticsItemToItem(product)
        ),
      },
    };
    this.#pushEventToDataLayer(data, GoogleAnalytics4EventNames.viewItem);
  };

  /**
   * @param {Object} viewItemList
   * @param {string} viewItemList.currencyCode
   * @param {Array.<AnalyticsItem>} viewItemList.productList
   * @param {string} viewItemList.listName
   * @param {string} viewItemList.listId
   */
  #viewItemList = ({ currencyCode, productList, listName, listId }) => {
    const items = productList.map((product) => {
      product.itemListName = listName;
      product.itemListId = listId;
      return this.#item.analyticsItemToItem(product);
    });

    this.#pushToViewItemListDataQueue(items, currencyCode);
  };

  /**
   * @param {Object} promotion
   * @param {Ad[]} promotion.bannerList
   */
  #viewPromotion = ({ bannerList }) => {
    this.#promotion.sendViewPromotionEvent(bannerList);
  };

  /**
   * @param {Object} viewProposal
   * @param {Array.<AnalyticsItem>} viewProposal.productList
   * @param {number} viewProposal.value
   */
  #viewProposal = ({ productList, value }) => {
    const data = {
      ecommerce: {
        currency: this.#currency,
        value,
        items: productList.map((product) =>
          this.#item.analyticsItemToItem(
            this.createCartItemObject({ product }),
            true
          )
        ),
      },
    };
    this.#pushEventToDataLayer(
      data,
      GoogleAnalytics4EventNames.viewCart,
      this.#updateSiteTypeDimension(PROPOSAL_DIMENSION)
    );
  };

  /**
   * @param {Product} cartProduct
   * @returns
   */
  createCartItemObject = ({ product, eventData }) => {
    const analytics = product.analytics;
    const quantity = analytics.quantity;

    const item = {
      product: product,
      quantity,
      activeProductId: product.id,
      ...eventData,
    };

    return item;
  };

  #sendPageNotFoundError = ({ location, referrer }) => {
    const data = {
      pageNotFound: {
        address: location,
        referrer,
      },
    };
    this.#pushEventToDataLayer(data, CustomEventNames.pageNotFound);
  };

  getSuccessData = (currentOrder) => {
    const purchase = new Purchase(
      this.#item,
      currentOrder,
      this.#activeCountry
    );

    purchase.createPurchaseEvent();

    return {
      ecommerce: {
        ...purchase.getPurchase(),
      },
    };
  };

  /**
   * Impressions come from multiple components, so we queue them instead of sending them right away.
   * @param items
   */
  #pushToViewItemListDataQueue = (items) => {
    this.#viewItemListDataQueue = this.#viewItemListDataQueue.concat(items);
    this.#requestViewItemListPushToDataLayer();
  };

  #pushViewItemListToDataLayer = () => {
    // GA has a maximum payload size of 8KB. If we have too many impressions we need to split them into chunks.
    const chunks = chunk(
      this.#viewItemListDataQueue,
      IMPRESSION_MAX_BATCH_SIZE
    );
    this.#pushChunksToDataLayer(
      chunks,
      GoogleAnalytics4EventNames.viewItemList
    );
    this.#viewItemListDataQueue = [];
  };

  /**
   *
   * @param {Array} chunks
   * @param {string} event
   */
  #pushChunksToDataLayer = (chunks, event) => {
    chunks.forEach((chunk) =>
      this.#pushEventToDataLayer(
        {
          ecommerce: {
            currency: this.#currency,
            items: chunk,
          },
        },
        event
      )
    );
  };

  /**
   * @param {Object} dimension
   * @param {string} dimension.dimensionKey
   * @param {string} dimension.dimensionValue
   */
  #updateSiteTypeDimension = ({ dimensionKey, dimensionValue }) => {
    if (this.#configStore.siteConfig.isHybrid) {
      window.dataLayer.push({ [dimensionKey]: dimensionValue });
    }
  };

  /**
   *
   * @param {Object} data
   * @param {GoogleAnalytics4EventNames} action
   * @param {Function} updateDimensionCallback
   */
  #pushEventToDataLayer = (data, action, updateDimensionCallback) => {
    window.dataLayer.push({
      event: action,
      ...data,
    });
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    updateDimensionCallback && updateDimensionCallback();
  };
}
